import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { MdOutlineClear } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IRole } from '~/types/role'

import AddAuthsModal from './AddAuthsModal'

import Section, { SectionItem } from '../../../shared/Section'

import LayoutGroups from '~/components/Layouts/Groups'
import ModalDialog from '~/components/ModalDialog'
import UserCard from '~/components/UserCard'
import { keys as rolesKeys, useDeleteAuthsInRoleMutation } from '~/queries/user-roles'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  role: IRole
}

const AuthDetailsGroups = ({ role }: Props): ReactElement => {
  /** Local state */

  const { authorizations = [], groups = [], role_id: roleId, users: specificOwners = [] } = role

  const queryClient = useQueryClient()

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const [isAuthsModalOpen, setAuthsModalOpen] = useState(false)

  const [deletedItemId, setDeletedItemId] = useState<string | undefined>()

  /** Queries & Mutations */

  const { mutateAsync: deleteAuthsMutation } = useDeleteAuthsInRoleMutation()

  /** Handlers */

  const handleOpenAuthsModal = (value: boolean) => () => setAuthsModalOpen(value)

  const handleAddGroupsFailure = (e: unknown) => {
    captureException(e)
    setAuthsModalOpen(false)
    displayToast('Authorizations could not be added', 'error')
  }

  const handleAddGroupsSuccess = () => {
    setAuthsModalOpen(false)
    displayToast('Authorizations successfully added', 'success')
  }

  const handleOpenDeleteModal = (value: boolean, authKey?: string) => () => {
    setDeletedItemId(authKey)
    setDeleteModalOpen(value)
  }

  const handleDelete = async () => {
    if (deletedItemId) {
      try {
        await deleteAuthsMutation({ authKeys: [deletedItemId], roleId: roleId })
        await queryClient.invalidateQueries({ queryKey: rolesKeys.details(roleId) })
        displayToast('Authorization successfully removed', 'success')
      } catch (e) {
        captureException(e)
        displayToast('Authorization could not be removed', 'error')
      }
    }
    setDeletedItemId(undefined)
    setDeleteModalOpen(false)
  }

  /** Render */

  const roleAuthorizations = authorizations.map(({ key }) => key)

  return (
    <>
      <LayoutGroups>
        <Section
          className="role-section"
          itemsCount={authorizations.length}
          onClickAdd={handleOpenAuthsModal(true)}
          title="Authorization"
        >
          {authorizations.map(({ key }) => (
            <SectionItem
              className="role-section__authorization-section-item"
              key={key}
              onDelete={handleOpenDeleteModal(true, key)}
            >
              <div className="item-body">
                <div className="item-title">{key}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
        <Section itemsCount={groups.length} title="Owner groups">
          {groups.map(({ description, group_name, id }) => (
            <SectionItem key={id}>
              <div className="item-body">
                <div className="item-title">{group_name}</div>
                <div className="item-subtitle">{description}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
        <Section className="role-section" itemsCount={specificOwners ? specificOwners.length : 0} title="Member users">
          {specificOwners.map(({ email, name, picture, profile, user_id }) => (
            <SectionItem key={user_id}>
              <div className="user-row">
                <UserCard name={name} picture={picture} size="small" subInfo={profile?.job_title} />
                <div className="user-email">{email}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
      </LayoutGroups>
      <AddAuthsModal
        isOpen={isAuthsModalOpen}
        onClose={handleOpenAuthsModal(false)}
        onFailure={handleAddGroupsFailure}
        onSuccess={handleAddGroupsSuccess}
        roleAuthorizations={roleAuthorizations}
        roleId={roleId}
      />
      <ModalDialog
        icon={<MdOutlineClear size={42} />}
        isOpen={isDeleteModalOpen}
        mainText="Remove authorization"
        onClose={handleOpenDeleteModal(false)}
        onConfirm={handleDelete}
        subText="Are you sure you want to remove the authorization from the role?"
      />
    </>
  )
}

export default AuthDetailsGroups
